import { editorialStatusBlock, hairlineRow, severityTicks, iconButton } from "../../design/components.js";
import { helperModeCard } from "./helper-mode.js";
import { requestSheet } from "./request-sheet.js";
import { cellDistanceKm, type HelpRequestView, type RequestState } from "./help-model.js";
import type { AvailabilityInput, CreateInput } from "./help-publish.js";
import { checkingLine, stalledLine, loadStateOf } from "../zone-checking.js";
import type { ZoneLoadState } from "../../app/zone-load-gate.js";

export interface NearbyMatch { requestId: string; need: string; urgency: number; cell: string; }

export interface HelpTabCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  homeCell: string;
  parentCell: string;
  tierCaption: string;
  nowMs: number;
  open: HelpRequestView[];
  activity: { requests: HelpRequestView[]; helping: HelpRequestView[]; offered?: HelpRequestView[] };
  /** My DID — to tell my own (pending) offer apart on an "offered" request. */
  me?: string;
  nearby: NearbyMatch[];
  onRegisterAvailability: (input: AvailabilityInput) => Promise<void>;
  onOpenRequest: (requestId: string) => void;
  onCreateRequest: (input: CreateInput) => Promise<void>;
  /** Wire a freshly-opened modal into the app's back-button stack (Android/browser back
   *  closes it). Given the modal's raw teardown, returns the close() to use as onClose. */
  onModalBack?: (close: () => void) => () => void;
  /** Returns true when the given request has unread coordination notes. */
  unread?: (requestId: string) => boolean;
  /** False while the firehose backfill is still in flight; omit for legacy callers. */
  loaded?: boolean;
  /** Tri-state load state; wins over `loaded` when present (`stalled` = still trying). */
  loadState?: ZoneLoadState;
  /** Manual retry once stalled (re-opens the help stream). */
  onRetry?: () => void;
}

function statusChip(state: RequestState): string {
  switch (state) {
    case "in_progress": return "help.status.chip.accepted";
    case "en_route": return "help.status.chip.enroute";
    case "arrived": return "help.status.chip.arrived";
    case "partial": return "help.status.chip.partial";
    case "done": return "help.status.chip.done";
    case "cancelled": return "help.status.chip.cancelled";
    default: return "help.status.chip.open"; // open / reopened
  }
}

/** Help tab (GUI §4): status block + helper-mode card + nearby-matches section + open-requests list + my-activity,
 *  with a create-request FAB. Nearby matches arrive via the private SSE stream (matcher A7 push). */
export function helpTab(ctx: HelpTabCtx): HTMLElement {
  const t = ctx.t;
  const root = document.createElement("section");
  root.className = "screen help-tab";

  // --- Status block ---
  const n = ctx.open.length;
  let headline: string;
  let subline: string;
  if (n === 0) {
    headline = t("help.status.none");
    subline = t("help.status.subNone");
  } else {
    headline = n === 1 ? t("help.status.one") : t("help.status.some", { n });
    const nearest = ctx.open[0];
    const dist = ctx.homeCell && nearest.cell ? cellDistanceKm(ctx.homeCell, nearest.cell) : 0;
    subline = t("help.status.sub", { need: t(`need.${nearest.need}`).toLowerCase(), distance: dist.toFixed(1) });
  }
  // Headline stays on one line via CSS (nowrap + vw-scaled clamp); see app.css.
  root.append(editorialStatusBlock({ microlabel: t("help.status.step"), headline, subline }));

  // --- Network/load status (checking / can't reach) — at the top, where a person looks first ---
  const loadState = loadStateOf(ctx.loadState, ctx.loaded);
  if (loadState === "checking") root.append(checkingLine(t));
  else if (loadState === "stalled") root.append(stalledLine(t, ctx.onRetry));

  // --- Helper-mode card ---
  root.append(helperModeCard({
    t, homeCell: ctx.homeCell, parentCell: ctx.parentCell, tierCaption: ctx.tierCaption,
    nowMs: ctx.nowMs, onRegister: ctx.onRegisterAvailability,
  }));

  // --- Requests near you (matcher A7 push, private SSE) ---
  const nearby = document.createElement("div");
  nearby.className = "help-nearby";
  const nearTitle = document.createElement("div");
  nearTitle.className = "bx-microlabel";
  nearTitle.textContent = t("help.nearby.title");
  nearby.append(nearTitle);
  if (ctx.nearby.length === 0) {
    const empty = document.createElement("p");
    empty.className = "bx-body";
    empty.textContent = t("help.nearby.empty");
    nearby.append(empty);
  } else {
    for (const mt of ctx.nearby) {
      const dist = ctx.homeCell && mt.cell ? cellDistanceKm(ctx.homeCell, mt.cell) : 0;
      const open = document.createElement("button");
      open.type = "button";
      open.className = "help-list__open";
      open.setAttribute("data-open-match", mt.requestId);
      open.append(hairlineRow({
        leading: t(`need.${mt.need}`),
        trailing: `${dist.toFixed(1)} km`,
        tone: "aid",
      }));
      open.append(severityTicks(mt.urgency, 3));
      open.addEventListener("click", () => ctx.onOpenRequest(mt.requestId));
      nearby.append(open);
    }
  }
  root.append(nearby);

  // --- Open requests list ---
  const list = document.createElement("div");
  list.className = "help-list";
  const listTitle = document.createElement("div");
  listTitle.className = "bx-microlabel";
  listTitle.textContent = t("help.list.title");
  list.append(listTitle);
  if (ctx.open.length === 0) {
    if (loadState === "loaded") { // while checking/stalled the top status line says so
      const empty = document.createElement("p");
      empty.className = "bx-body";
      empty.textContent = t("help.list.empty");
      list.append(empty);
    }
  } else {
    for (const r of ctx.open) {
      const dist = ctx.homeCell && r.cell ? cellDistanceKm(ctx.homeCell, r.cell) : 0;
      const open = document.createElement("button");
      open.type = "button";
      open.className = "help-list__open";
      open.setAttribute("data-open-request", r.requestId);
      open.append(hairlineRow({
        leading: t(`need.${r.need}`),
        trailing: `${dist.toFixed(1)} km`,
        tone: "aid",
      }));
      open.append(severityTicks(r.urgency, 3));
      open.addEventListener("click", () => ctx.onOpenRequest(r.requestId));
      list.append(open);
    }
  }
  root.append(list);

  // --- My activity (Active newest-first, then Resolved, badged) ---
  const TERMINAL = new Set<RequestState>(["done", "cancelled"]);
  const mineIds = new Set(ctx.activity.requests.map((r) => r.requestId));
  const offeredIds = new Set((ctx.activity.offered ?? []).map((r) => r.requestId));
  const all = [...ctx.activity.requests, ...ctx.activity.helping, ...(ctx.activity.offered ?? [])];
  const byRecency = (a: HelpRequestView, b: HelpRequestView) => b.lastSeq - a.lastSeq;
  const active = all.filter((r) => !TERMINAL.has(r.state)).sort(byRecency);
  const resolved = all.filter((r) => TERMINAL.has(r.state)).sort(byRecency);

  const activityCard = (r: HelpRequestView): HTMLElement => {
    const mine = mineIds.has(r.requestId);
    const offered = !mine && offeredIds.has(r.requestId);
    // Pending = folded locally at publish time, not yet echoed by the firehose (mine: the
    // request itself; offered: my offer on it). Tinted + labelled until the echo clears it.
    const pending = mine ? !!r.pending : offered ? r.offers.some((o) => o.helper === ctx.me && o.pending) : false;
    const card = document.createElement("button");
    card.type = "button";
    card.className = "help-activity__card";
    card.setAttribute("data-open-thread", r.requestId);
    if (pending) card.setAttribute("data-pending", "");
    if (ctx.unread?.(r.requestId)) {
      const dot = document.createElement("span");
      dot.className = "help-activity__unread";
      dot.setAttribute("data-unread", "");
      card.append(dot);
    }
    const role = mine ? t("help.activity.mine") : offered ? t("help.activity.offered") : t("help.activity.helping");
    card.append(hairlineRow({
      leading: `${role} · ${t(`need.${r.need}`)}`,
      trailing: pending ? t("help.activity.pending") : t(statusChip(r.state)),
      tone: "aid",
    }));
    card.addEventListener("click", () => ctx.onOpenRequest(r.requestId));
    return card;
  };

  if (active.length > 0 || resolved.length > 0) {
    const activity = document.createElement("div");
    activity.className = "help-activity";
    const aTitle = document.createElement("div");
    aTitle.className = "bx-microlabel";
    aTitle.textContent = t("help.activity.title");
    activity.append(aTitle);
    if (active.length > 0) {
      const group = document.createElement("div");
      group.className = "help-activity__active";
      const gt = document.createElement("div");
      gt.className = "bx-microlabel";
      gt.textContent = t("help.activity.activeTitle");
      group.append(gt);
      for (const r of active) group.append(activityCard(r));
      activity.append(group);
    }
    if (resolved.length > 0) {
      const group = document.createElement("div");
      group.className = "help-activity__resolved";
      const gt = document.createElement("div");
      gt.className = "bx-microlabel";
      gt.textContent = t("help.activity.resolvedTitle");
      group.append(gt);
      for (const r of resolved) group.append(activityCard(r));
      activity.append(group);
    }
    root.append(activity);
  }

  // --- Create-request FAB — SVG life-ring glyph, opens the ask-for-help modal ---
  const fab = iconButton({
    label: t("help.create.button"),
    icon: "ask-help",
    variant: "primary",
    onClick: () => {
      if (root.querySelector("[data-sheet=create-request]")) return;
      let sheet: HTMLElement;
      const rawClose = (): void => sheet.remove();
      const close = ctx.onModalBack ? ctx.onModalBack(rawClose) : rawClose;
      sheet = requestSheet({
        t, defaultCell: ctx.homeCell,
        onSubmit: ctx.onCreateRequest,
        onClose: close,
      });
      root.append(sheet);
    },
  });
  fab.classList.add("report-fab");
  fab.setAttribute("data-action", "create-request");
  root.append(fab);

  return root;
}
